import { ChangedPlanRecord, Spot } from "../../types"
import {
  FormulaOperator,
  FormulaSet,
  MediaField,
  MediaType,
} from "routes/Base/Admin/GlobalMediaTypes/types"
import { add, divide, get, isEqual, multiply, set, subtract } from "lodash/fp"
import bigDecimal from "js-big-decimal"
import { BaseRateRowRecord } from "models/ratecard"
import { BillingType } from "routes/Base/Admin/MarketSettings/List/BillingTypes/types"
import { MediaFields } from "./types"
import { calculationContext } from "./calculations"
import { eachDayOfInterval } from "date-fns"
import { utcToZonedTime } from "date-fns-tz"

const TIME_RGX = new RegExp(
  /^(?:(?:([01]?\d|2[0-3]):)?([0-5]?\d):)?([0-5]?\d)$/g,
)
export const parseTime = (s?: string) => {
  const timeMatch = !!s?.match(TIME_RGX)
  const parts = s?.split(":")
  const _parts = parts?.map((p) => p?.padStart(2, "0"))
  if (timeMatch && _parts?.length === 3) {
    return s
  }
  if (timeMatch && _parts?.length === 2) {
    return _parts.concat("00").join(":")
  }
}

export function getMediaField(code: string) {
  const cc = calculationContext
  const media = cc.media
  return media?.mediaFields?.find((f) => f?.semanticCode === code)
}

export function getMediaFieldBySemantic(
  semantic: string,
  externalContext?: { media: MediaType },
) {
  const cc = externalContext || calculationContext
  const fieldId = cc.media?.defaultFieldIdBySemantic?.[semantic]
  const field = cc.media?.mediaFields?.find((mf) => mf.id === fieldId)

  return field
}

export function getMediaFieldDaily(fields: MediaFields, field?: MediaField) {
  if (!fields || !field) return undefined

  const { loadings, applyLoadings, timeZone } = calculationContext
  const value: Record<string, number> = {}

  switch (field.type) {
    case "VariableCost": {
      if (!get([field.id, "defaultCost"], fields)) {
        return undefined
      }

      const cost = Number(get([field.id, "defaultCost"], fields) ?? 0)
      value["defaultCost"] = cost
      if (!loadings || !applyLoadings) return value

      loadings.forEach((l) => {
        const start = l.validationPeriodStart
        const end = l.validationPeriodEnd
        if (!!start && !!end) {
          const days = eachDayOfInterval({
            start: utcToZonedTime(new Date(start), timeZone),
            end: utcToZonedTime(new Date(end), timeZone),
          })
          const percentageFactor = (l.percentage ?? 0) * 0.01
          days?.forEach(
            (day) =>
              (value[day.toISOString()] = roundHalfEven(
                Number(cost + cost * percentageFactor),
              )),
          )
        }
      })

      return value
    }
    case "SpotsPlan":
      {
        const spots = get([field.id, "spots"], fields) as Spot[]
        spots?.forEach((spot) => {
          const sd = utcToZonedTime(spot.date, timeZone).toISOString()
          value[sd] = spot.quantity
        })
      }
      break
    default:
      break
  }

  return value
}

export function getMediaFieldValue<T>(fields: MediaFields, field?: MediaField) {
  if (!fields || !field) return undefined

  let v, value

  switch (field.type) {
    case "NumericWithUnit":
      v = get([field.id, "value"], fields)
      value = !!v ? Number(v) : undefined
      break
    case "VariableCost":
      v = get([field.id, "defaultCost"], fields)
      value = !!v ? Number(v) : undefined
      break
    case "SpotsPlan":
      {
        const spots = get([field.id, "spots"], fields) as Array<Spot>
        value =
          spots?.reduce((sum, spot) => {
            sum += spot.quantity

            return sum
          }, 0) || 0
      }
      break
    case "Commission":
    case "Fees":
      value = get([field.id], fields)
      break
    case "BillingType":
      {
        const billingTypeId = get([field.id, "billingTypeId"], fields)
        const billingSemantic = get([field.id, "semantic"], fields)
        if (billingTypeId) {
          const cc = calculationContext
          value = cc.billingTypes
            ? (cc.billingTypes?.find((bt) => bt.id === billingTypeId)
                ?.semanticCode as BillingType["semanticCode"])
            : billingSemantic
        }
      }
      break
    case "Taxes":
      value = get([field.id], fields) || {}
      break
    default:
      value = get([field.id], fields)
      break
  }
  return value as T
}

export function setMediaFieldValue(
  value: any,
  fields: MediaFields,
  field: MediaField,
  path: string[] = [],
) {
  if (!fields) return fields

  let _fields = fields
  switch (field.type) {
    case "NumericWithUnit":
    case "Fees":
    case "Commission":
      _fields = set([field.id, ...path], value, fields)
      break
    case "VariableCost":
      _fields = set([field.id, "defaultCost"], value, fields)
      break
    case "Taxes":
      _fields = set([field.id, ...path], value, fields)
      break
    case "HourInterval":
      _fields = set([field.id, ...path], value, fields)
      break
    default:
      _fields = set([field.id, ...path], value, fields)
  }

  return _fields
}

export function copyBuyingToPlanning(
  li: ChangedPlanRecord,
  media?: MediaType,
  transparent?: boolean,
): ChangedPlanRecord {
  let copied = li

  if (transparent) {
    copied = {
      ...copied,
      planningMediaFields: {
        ...copied.planningMediaFields,
        ...copied.buyingMediaFields,
      },
    }
  } else {
    if (!media) return copied
    const {
      grossCost: gcId,
      unitCost: ucId,
      billingType: btId,
    } = media.defaultFieldIdBySemantic || {}

    const {
      [gcId]: _gc,
      [ucId]: _uc,
      [btId]: _bt,
      ...buying
    } = copied.buyingMediaFields || {}

    copied = {
      ...copied,
      planningMediaFields: { ...copied.planningMediaFields, ...buying },
    }
  }

  return copied
}

export function operatorToFunction(operator?: FormulaOperator) {
  let op
  switch (operator) {
    case "+":
      op = add
      break
    case "*":
      op = multiply
      break
    case "-":
      op = subtract
      break
    case "/":
      op = divide
      break
    default:
      op = add
  }

  return op
}

function toPrecision(n: number, d: number = 5) {
  return Math.trunc(n * Math.pow(10, d)) / Math.pow(10, d)
}

/*
  from:
  https://stackoverflow.com/a/49080858
*/
export function roundHalfEven(n: number, d: number = 2) {
  const maxPrecision = d > 5 ? d + 1 : 5
  const pn = toPrecision(n, maxPrecision)
  const x = pn * Math.pow(10, d)
  const r = Math.round(x)
  const br = Math.abs(x) % 1 === 0.5 ? (r % 2 === 0 ? r : r - 1) : r

  const rounded = br / Math.pow(10, d)
  return parseFloat(rounded.toFixed(d))
}

export function getAppliedPercentageOrFixed(
  target: number,
  amount: number,
  amountType?: "PERCENTAGE" | "FIXED_AMOUNT" | "MONETARY" | "NUMERIC",
  precision?: number,
) {
  if (amountType !== "PERCENTAGE") return Number(amount)

  const percentaged = parseFloat(bigDecimal.multiply(amount, target * 0.01))
  return roundHalfEven(percentaged, precision)
}

/* Rate cards */
export const NON_REQUIRED_RATE_CARD_DIMENSIONS = ["timebelt"]

type RateCardPredicateFn = (
  br: BaseRateRowRecord,
  value: any,
  key: keyof BaseRateRowRecord,
  extra?: Record<string, any>,
) => boolean

export const RATE_DIMENSION_CHECK: Record<
  keyof BaseRateRowRecord,
  RateCardPredicateFn
> = {
  timebelt: (br, value, key, extra) => {
    const startTimeMatch =
      br[key]?.start <= (extra?.start ?? "") &&
      br[key]?.end >= (extra?.start ?? "")
    if (startTimeMatch) return true

    return !extra?.start && isEqual(br[key], value)
  },
  daysOfWeek: (br, value, key) =>
    (value as string[]).every((v) => br[key].includes(v)),
  default: (br, value, key) => !!key && isEqual(br[key], value),
}

export function getDimensionEqualityCheck(key: keyof BaseRateRowRecord) {
  const perKey = RATE_DIMENSION_CHECK[key]

  return perKey || RATE_DIMENSION_CHECK["default"]
}

export const systemFormulas = () => {
  const unitCostId = getMediaField("unitCost")?.id
  const spotsId = getMediaField("spots")?.id
  const grpBId = getMediaField("grpB")?.id

  const grossCostCppFormula = {
    formulaParts: [
      {
        leftOperand: { mediaFieldId: unitCostId },
        operator: "*",
        rightOperand: { mediaFieldId: spotsId },
        partOperator: "*",
      },
      {
        leftOperand: { mediaFieldId: grpBId },
        operator: "*",
        rightOperand: { fixedValue: 1 },
      },
    ],
  } as FormulaSet
  return { grossCostCppFormula }
}
